import {
  preferencesToUppKonvaAreaOrientation,
  type FloorPlanDrawItemLabels,
  type FloorPlanAreaSourceDrawPieceItem,
  type FloorPlanAreaSourceDrawPieceType,
  type FloorPlanAreaSourceTable,
  type FloorPlanAreaSourceVertex,
  type UppKonvaArea,
  type UppKonvaAreaOrientation,
  type UppKonvaAreaSpatialMode,
  type UppKonvaShape,
} from '../adapters/floor-plan-from-area-source';
import { resolveFloorPlanDrawRowUuid } from '../../internal/floor-plan-uuid';
import {
  mergeUppFloorPlanPreferences,
  UPP_FLOOR_PLAN_PREFERENCES_DEFAULT,
  type UppFloorPlanPreferences,
} from '../floor-plan-preferences';
import { UppFloorPlanModel } from '../floor-plan-model';
import {
  UppFloorPlanArchitecture,
  UppFloorPlanBar,
  UppFloorPlanFurniture,
  UppFloorPlanVertex,
  UppFloorPlanTable,
  type UppFloorPlanBarShape,
  type UppFloorPlanElement,
  type UppFloorPlanSofaShape,
} from '../floor-plan-element';

/** Wire format discriminator for floor-plan JSON files. */
export const UPP_FLOOR_PLAN_DOCUMENT_KIND = 'unpispas/upp-konva/floor-plan' as const;

/**
 * English comment normalized.
 * English comment normalized.
 */
export const UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION = 11 as const;

/**
 * English comment normalized.
 * English comment normalized.
 */
export const UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V10 = 10 as const;

/**
 * English comment normalized.
 * English comment normalized.
 */
export const UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V9 = 9 as const;

/**
 * English comment normalized.
 */
export const UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V1 = 1 as const;

const SUPPORTED_SCHEMA_VERSIONS: ReadonlySet<number> = new Set<number>([
  UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION,
  UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V10,
  UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V9,
]);

export type UppFloorPlanDocumentSchemaVersion = typeof UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION;

/**
 * Versioned floor-plan exchange document (no file I/O).
 * Integrators add download / upload and permission checks in the app layer.
 */
export interface UppFloorPlanDocument {
  readonly kind: typeof UPP_FLOOR_PLAN_DOCUMENT_KIND;
  readonly schemaVersion: UppFloorPlanDocumentSchemaVersion;
  /** ISO-8601 timestamp; optional. */
  readonly exportedAt?: string;
  readonly area: UppKonvaArea;
}

export type UppFloorPlanParseDocumentResult =
  | { readonly ok: true; readonly area: UppKonvaArea }
  | { readonly ok: false; readonly errorCode: string; readonly message: string };

const DRAW_ITEM_TYPES = new Set<string>([
  'VERTEX',
  'TABLE',
  'BAR',
  'DOOR',
  'WINDOW',
  'STAIRS',
  'SOFA',
  'BILLIARD',
  'FOOTBALL',
  'GENERIC',
]);

const AREA_ORIENTATIONS = new Set<UppKonvaAreaOrientation>(['PORTRAIT', 'LANDSCAPE', 'AUTO']);

const AREA_SPATIAL_MODES = new Set<UppKonvaAreaSpatialMode>(['SHAPES', 'VERTEX']);

const BAR_SHAPES = new Set<UppFloorPlanBarShape>(['LINEAR', 'TOLEFT', 'TORIGHT', 'USHAPE']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asFiniteNumber(v: unknown): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    return null;
  }
  return v;
}

function asOptionalString(v: unknown): string | undefined {
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v !== 'string') {
    return undefined;
  }
  return v;
}

function readX(raw: Record<string, unknown>): number | null {
  return asFiniteNumber(raw['x']);
}

function readY(raw: Record<string, unknown>): number | null {
  return asFiniteNumber(raw['y']);
}

function readWidth(raw: Record<string, unknown>): number | null {
  return asFiniteNumber(raw['width']);
}

function readHeight(raw: Record<string, unknown>): number | null {
  return asFiniteNumber(raw['height']);
}

function readRotation(raw: Record<string, unknown>): number | null {
  return asFiniteNumber(raw['rotation']);
}

/** Present in JSON and must be finite; `undefined` if key absent. */
function readOptionalWidth(raw: Record<string, unknown>): number | undefined | false {
  if (!Object.prototype.hasOwnProperty.call(raw, 'width')) {
    return undefined;
  }
  const w = readWidth(raw);
  return w === null ? false : w;
}

function readOptionalHeight(raw: Record<string, unknown>): number | undefined | false {
  if (!Object.prototype.hasOwnProperty.call(raw, 'height')) {
    return undefined;
  }
  const h = readHeight(raw);
  return h === null ? false : h;
}

function readOptionalRotation(raw: Record<string, unknown>): number | undefined | false {
  if (!Object.prototype.hasOwnProperty.call(raw, 'rotation')) {
    return undefined;
  }
  const r = readRotation(raw);
  return r === null ? false : r;
}

/** Labels and `attention` only in row properties (no options bag). */
function parseDrawRowLabels(raw: Record<string, unknown>): {
  label?: string;
  sublabel?: string;
  custom?: unknown;
  attention?: boolean;
} {
  const out: { label?: string; sublabel?: string; custom?: unknown; attention?: boolean } = {};
  if (typeof raw['label'] === 'string') {
    out.label = raw['label'];
  }
  if (typeof raw['sublabel'] === 'string') {
    out.sublabel = raw['sublabel'];
  }
  if (Object.prototype.hasOwnProperty.call(raw, 'custom')) {
    out.custom = raw['custom'];
  }
  if (raw['attention'] === true) {
    out.attention = true;
  }
  return out;
}

function spreadLabelsNoAttention(
  labels: ReturnType<typeof parseDrawRowLabels>,
): FloorPlanDrawItemLabels {
  const r: Record<string, unknown> = {};
  if (labels.label !== undefined) {
    r['label'] = labels.label;
  }
  if (labels.sublabel !== undefined) {
    r['sublabel'] = labels.sublabel;
  }
  if (labels.custom !== undefined) {
    r['custom'] = labels.custom;
  }
  return r as FloorPlanDrawItemLabels;
}

function packDrawLabels(el: UppFloorPlanElement): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  if (el.label !== '') {
    o['label'] = el.label;
  }
  if (el.sublabel !== '') {
    o['sublabel'] = el.sublabel;
  }
  if (el.custom != null) {
    o['custom'] = el.custom;
  }
  if (el instanceof UppFloorPlanTable && el.attention) {
    o['attention'] = true;
  }
  return o;
}

function parseVertex(raw: unknown, index: number): FloorPlanAreaSourceVertex | null {
  void index;
  if (!isRecord(raw)) {
    return null;
  }
  if (
    Object.prototype.hasOwnProperty.call(raw, 'polygonId') ||
    Object.prototype.hasOwnProperty.call(raw, 'chainClosed')
  ) {
    return null;
  }
  const x = readX(raw);
  const y = readY(raw);
  const sort = asFiniteNumber(raw['sort']);
  if (x === null || y === null || sort === null) {
    return null;
  }
  const labels = spreadLabelsNoAttention(parseDrawRowLabels(raw));
  const polyCanon = typeof raw['poligon'] === 'string' ? raw['poligon'].trim() : '';
  const polyLegacy = typeof raw['polygon'] === 'string' ? raw['polygon'].trim() : '';
  const polygon = polyCanon.length > 0 ? polyCanon : polyLegacy.length > 0 ? polyLegacy : undefined;
  const closed = raw['closed'] === true;
  const zi = parseOptionalZIndex(raw);
  if (zi === false) {
    return null;
  }
  const uuid = resolveFloorPlanDrawRowUuid(raw);
  const ow = readOptionalWidth(raw);
  if (ow === false) {
    return null;
  }
  const oh = readOptionalHeight(raw);
  if (oh === false) {
    return null;
  }
  const orot = readOptionalRotation(raw);
  if (orot === false) {
    return null;
  }
  const base: FloorPlanAreaSourceVertex = { uuid, x, y, sort, ...labels };
  const withPoly: FloorPlanAreaSourceVertex = polygon !== undefined ? { ...base, polygon } : base;
  const withClosed: FloorPlanAreaSourceVertex = closed ? { ...withPoly, closed: true } : withPoly;
  const withGeom: FloorPlanAreaSourceVertex = {
    ...withClosed,
    ...(ow !== undefined ? { width: ow } : {}),
    ...(oh !== undefined ? { height: oh } : {}),
    ...(orot !== undefined ? { rotation: orot } : {}),
  };
  return zi !== undefined ? { ...withGeom, zIndex: zi } : withGeom;
}

function parseTable(raw: unknown, index: number): FloorPlanAreaSourceTable | null {
  void index;
  if (!isRecord(raw)) {
    return null;
  }
  const x = readX(raw);
  const y = readY(raw);
  const width = readWidth(raw);
  const height = readHeight(raw);
  const rotation = readRotation(raw);
  if (x === null || y === null || width === null || height === null || rotation === null) {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(raw, 'chairCount')) {
    return null;
  }
  const chairsPick = raw['chairs'];
  let chairs: number | undefined;
  if (chairsPick !== undefined) {
    if (typeof chairsPick !== 'number' || !Number.isFinite(chairsPick) || chairsPick < 0) {
      return null;
    }
    chairs = Math.trunc(chairsPick);
  }
  const dinersPick = raw['diners'];
  let diners: number | undefined;
  if (dinersPick !== undefined) {
    if (typeof dinersPick !== 'number' || !Number.isFinite(dinersPick) || dinersPick < 0) {
      return null;
    }
    diners = Math.trunc(dinersPick);
  }
  const shapeRaw = raw['shape'];
  let shape: 'SQUARE' | 'CIRCLE' | undefined;
  if (shapeRaw !== undefined) {
    if (shapeRaw !== 'SQUARE' && shapeRaw !== 'CIRCLE') {
      return null;
    }
    shape = shapeRaw;
  }
  const merged = parseDrawRowLabels(raw);
  const zi = parseOptionalZIndex(raw);
  if (zi === false) {
    return null;
  }
  const uuid = resolveFloorPlanDrawRowUuid(raw);
  const row: FloorPlanAreaSourceTable = {
    uuid,
    x,
    y,
    width,
    height,
    rotation,
    ...(chairs !== undefined ? { chairs } : {}),
    ...(diners !== undefined ? { diners } : {}),
    shape,
    ...spreadLabelsNoAttention(merged),
    ...(merged.attention === true ? { attention: true } : {}),
    ...(zi !== undefined ? { zIndex: zi } : {}),
  };
  return row;
}

const PIECE_TYPES = new Set<FloorPlanAreaSourceDrawPieceType>([
  'BAR',
  'DOOR',
  'WINDOW',
  'STAIRS',
  'SOFA',
  'BILLIARD',
  'FOOTBALL',
  'GENERIC',
]);

function parseDrawPieceItem(
  raw: Record<string, unknown>,
  type: FloorPlanAreaSourceDrawPieceType,
  index: number,
): FloorPlanAreaSourceDrawPieceItem | null {
  void index;
  const x = readX(raw);
  const y = readY(raw);
  const width = readWidth(raw);
  const height = readHeight(raw);
  const rotation = readRotation(raw);
  if (x === null || y === null || width === null || height === null || rotation === null) {
    return null;
  }
  const zi = parseOptionalZIndex(raw);
  if (zi === false) {
    return null;
  }
  if (type !== 'BAR' && Object.prototype.hasOwnProperty.call(raw, 'shape')) {
    return null;
  }
  const zSpread = zi !== undefined ? { zIndex: zi } : {};
  const labels = spreadLabelsNoAttention(parseDrawRowLabels(raw));
  const uuid = resolveFloorPlanDrawRowUuid(raw);
  if (type === 'BAR') {
    const barShapeStr = asOptionalString(raw['shape']);
    const barShape = (barShapeStr ?? 'LINEAR') as UppFloorPlanBarShape;
    if (!BAR_SHAPES.has(barShape)) {
      return null;
    }
    const LEGACY_STOOL_KEYS = ['stoolFrontal', 'stoolCountLeft', 'stoolCountFront', 'stoolCountRight'] as const;
    if (LEGACY_STOOL_KEYS.some((k) => Object.prototype.hasOwnProperty.call(raw, k))) {
      return null;
    }
    const stoolLeftRaw = raw['stoolLeft'];
    const stoolFrontRaw = raw['stoolFront'];
    const stoolRightRaw = raw['stoolRight'];
    const stoolLeft = parseOptionalNonNegativeInt(stoolLeftRaw);
    const stoolFront = parseOptionalNonNegativeInt(stoolFrontRaw);
    const stoolRight = parseOptionalNonNegativeInt(stoolRightRaw);
    if (stoolLeft === false || stoolFront === false || stoolRight === false) {
      return null;
    }
    return {
      uuid,
      type: 'BAR',
      shape: barShape,
      x,
      y,
      width,
      height,
      rotation,
      stoolLeft: stoolLeft === undefined ? undefined : stoolLeft,
      stoolFront: stoolFront === undefined ? undefined : stoolFront,
      stoolRight: stoolRight === undefined ? undefined : stoolRight,
      ...labels,
      ...zSpread,
    };
  }
  if (type === 'GENERIC') {
    const genShape = asOptionalString(raw['furnitureShape']);
    if (genShape !== 'GENRCT' && genShape !== 'GENRND') {
      return null;
    }
    return {
      uuid,
      type: 'GENERIC',
      furnitureShape: genShape,
      x,
      y,
      width,
      height,
      rotation,
      ...labels,
      ...zSpread,
    };
  }
  if (type === 'SOFA') {
    const sofaStrRaw = asOptionalString(raw['furnitureShape']);
    const sofaShapeStr = sofaStrRaw === undefined || sofaStrRaw === null ? 'LINEAR' : sofaStrRaw;
    if (sofaShapeStr !== 'LINEAR' && sofaShapeStr !== 'TOLEFT' && sofaShapeStr !== 'TORIGHT') {
      return null;
    }
    const sofaShape = sofaShapeStr as UppFloorPlanSofaShape;
    return {
      uuid,
      type: 'SOFA',
      ...(sofaShape !== 'LINEAR' ? { furnitureShape: sofaShape } : {}),
      x,
      y,
      width,
      height,
      rotation,
      ...labels,
      ...zSpread,
    };
  }
  if (type === 'DOOR' || type === 'WINDOW' || type === 'STAIRS') {
    const architectureShape = asOptionalString(raw['architectureShape']);
    return {
      uuid,
      type,
      ...(architectureShape !== undefined ? { architectureShape } : {}),
      x,
      y,
      width,
      height,
      rotation,
      ...labels,
      ...zSpread,
    };
  }
  return {
    uuid,
    type,
    x,
    y,
    width,
    height,
    rotation,
    ...labels,
    ...zSpread,
  };
}

function parseDrawItem(raw: unknown, index: number): UppKonvaShape | null {
  if (!isRecord(raw)) {
    return null;
  }
  const type = raw['type'];
  if (typeof type !== 'string' || !DRAW_ITEM_TYPES.has(type)) {
    return null;
  }
  if (type === 'VERTEX') {
    const body = parseVertex(raw, index);
    return body ? { type: 'VERTEX', ...body } : null;
  }
  if (type === 'TABLE') {
    const body = parseTable(raw, index);
    return body ? { type: 'TABLE', ...body } : null;
  }
  if (!PIECE_TYPES.has(type as FloorPlanAreaSourceDrawPieceType)) {
    return null;
  }
  return parseDrawPieceItem(raw, type as FloorPlanAreaSourceDrawPieceType, index);
}

/** `undefined` = absent (default 0); `false` = invalid. */
function parseOptionalZIndex(raw: Record<string, unknown>): number | undefined | false {
  if (!Object.prototype.hasOwnProperty.call(raw, 'zIndex')) {
    return undefined;
  }
  const v = raw['zIndex'];
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    return false;
  }
  return Math.trunc(v);
}

/** `false` means invalid; `undefined` field means omit. */
function parseOptionalNonNegativeInt(v: unknown): number | undefined | false {
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) {
    return false;
  }
  return Math.trunc(v);
}

/**
 * @internal Inverse mapping for serialization tests and the {@link UppKonvaModel.area} getter — not part of the public package API.
 */
export function uppFloorPlanAreaSourceFromModel(
  model: UppFloorPlanModel,
  preferences?: Partial<UppFloorPlanPreferences> | null,
  spatialMode: UppKonvaAreaSpatialMode = 'SHAPES',
): UppKonvaArea {
  const shapes: UppKonvaShape[] = [];
  const zJson = (el: UppFloorPlanElement) => (el.zIndex !== 0 ? { zIndex: el.zIndex } : {});

  for (const el of model.elements) {
    if (el instanceof UppFloorPlanVertex) {
      const lb = packDrawLabels(el);
      shapes.push({
        type: 'VERTEX',
        uuid: el.uuid,
        x: el.xCm,
        y: el.yCm,
        sort: el.sort,
        ...(el.polygon !== 'default' ? { poligon: el.polygon } : {}),
        ...(el.closed ? { closed: true } : {}),
        ...zJson(el),
        ...lb,
      });
    } else if (el instanceof UppFloorPlanTable) {
      const lb = packDrawLabels(el);
      shapes.push({
        type: 'TABLE',
        uuid: el.uuid,
        x: el.xCm,
        y: el.yCm,
        width: el.widthCm,
        height: el.heightCm,
        rotation: el.rotationDeg,
        chairs: el.chairCount,
        ...(el.diners > 0 ? { diners: el.diners } : {}),
        shape: el.tableShape === 'ELLIPSE' ? 'CIRCLE' : 'SQUARE',
        ...zJson(el),
        ...lb,
      });
    } else if (el instanceof UppFloorPlanBar) {
      const lb = packDrawLabels(el);
      shapes.push({
        uuid: el.uuid,
        type: 'BAR',
        shape: el.barShape,
        x: el.xCm,
        y: el.yCm,
        width: el.widthCm,
        height: el.heightCm,
        rotation: el.rotationDeg,
        ...(el.stoolLeft !== undefined ? { stoolLeft: el.stoolLeft } : {}),
        ...(el.stoolFront !== undefined ? { stoolFront: el.stoolFront } : {}),
        ...(el.stoolRight !== undefined ? { stoolRight: el.stoolRight } : {}),
        ...zJson(el),
        ...lb,
      });
    } else if (el instanceof UppFloorPlanArchitecture) {
      const t: FloorPlanAreaSourceDrawPieceType =
        el.architectureClass === 'door'
          ? 'DOOR'
          : el.architectureClass === 'window'
            ? 'WINDOW'
            : 'STAIRS';
      const lb = packDrawLabels(el);
      shapes.push({
        uuid: el.uuid,
        type: t,
        architectureShape: el.architectureShape,
        x: el.xCm,
        y: el.yCm,
        width: el.widthCm,
        height: el.heightCm,
        rotation: el.rotationDeg,
        ...zJson(el),
        ...lb,
      });
    } else if (el instanceof UppFloorPlanFurniture) {
      const map: Record<UppFloorPlanFurniture['furnitureClass'], FloorPlanAreaSourceDrawPieceType> = {
        sofa: 'SOFA',
        billiard: 'BILLIARD',
        football: 'FOOTBALL',
        generic: 'GENERIC',
      };
      const ft = map[el.furnitureClass];
      const lb = packDrawLabels(el);
      if (ft === 'GENERIC') {
        shapes.push({
          uuid: el.uuid,
          type: 'GENERIC',
          furnitureShape: el.genericShape,
          x: el.xCm,
          y: el.yCm,
          width: el.widthCm,
          height: el.heightCm,
          rotation: el.rotationDeg,
          ...zJson(el),
          ...lb,
        });
      } else {
        shapes.push({
          uuid: el.uuid,
          type: ft,
          ...(ft === 'SOFA' && el.sofaShape !== 'LINEAR' ? { furnitureShape: el.sofaShape } : {}),
          x: el.xCm,
          y: el.yCm,
          width: el.widthCm,
          height: el.heightCm,
          rotation: el.rotationDeg,
          ...zJson(el),
          ...lb,
        });
      }
    }
  }

  const base: UppKonvaArea = {
    width: model.room.widthCm,
    height: model.room.heightCm,
    name: model.room.name,
    shapes,
    ...(spatialMode === 'VERTEX' ? { mode: 'VERTEX' as const } : {}),
  };

  if (preferences !== undefined && preferences !== null) {
    const p = mergeUppFloorPlanPreferences(preferences);
    return {
      ...base,
      orientation: preferencesToUppKonvaAreaOrientation(p),
      ...(p.viewZoom !== UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.viewZoom ? { viewZoom: p.viewZoom } : {}),
      ...(p.marginPx !== UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.marginPx ? { marginPx: p.marginPx } : {}),
      ...(p.pixelRatioMax !== UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.pixelRatioMax
        ? { pixelRatioMax: p.pixelRatioMax }
        : {}),
      ...(p.labelSize !== UPP_FLOOR_PLAN_PREFERENCES_DEFAULT.labelSize ? { labelSize: p.labelSize } : {}),
    };
  }
  return base;
}

export interface SerializeFloorPlanDocumentOptions {
  readonly exportedAt?: string;
}

/**
 * Serializes a {@link UppKonvaArea} to a JSON string (pretty-printed), **`schemaVersion`**: {@link UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION}
 * (`x`/`y`/`width`/`height`/`rotation` on draw rows). Prefer **`UppKonvaModel.exportModel`** when the live model is available.
 */
export function serializeFloorPlanDocument(
  area: UppKonvaArea,
  options?: SerializeFloorPlanDocumentOptions,
): string {
  const doc: UppFloorPlanDocument = {
    kind: UPP_FLOOR_PLAN_DOCUMENT_KIND,
    schemaVersion: UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION,
    exportedAt: options?.exportedAt,
    area: {
      width: area.width,
      height: area.height,
      ...(area.name !== undefined ? { name: area.name } : {}),
      ...(area.orientation !== undefined ? { orientation: area.orientation } : {}),
      ...(area.mode !== undefined ? { mode: area.mode } : {}),
      ...(area.viewZoom !== undefined ? { viewZoom: area.viewZoom } : {}),
      ...(area.marginPx !== undefined ? { marginPx: area.marginPx } : {}),
      ...(area.pixelRatioMax !== undefined ? { pixelRatioMax: area.pixelRatioMax } : {}),
      ...(area.labelSize !== undefined ? { labelSize: area.labelSize } : {}),
      shapes: [...area.shapes],
    },
  };
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/**
 * Parses and validates a floor-plan JSON document.
 * Returns a {@link UppKonvaArea} suitable for **`new UppKonvaModel(area)`**.
 */
/** `undefined` = absent; `false` = invalid. */
function parseOptionalPositiveFinite(areaRaw: Record<string, unknown>, key: string): number | undefined | false {
  const v = areaRaw[key];
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) {
    return false;
  }
  return v;
}

/** `undefined` = absent; `false` = invalid. */
function parseOptionalNonNegativeFinite(areaRaw: Record<string, unknown>, key: string): number | undefined | false {
  const v = areaRaw[key];
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) {
    return false;
  }
  return v;
}

function parseAreaRoomFields(areaRaw: Record<string, unknown>): {
  readonly width: number;
  readonly height: number;
  readonly name: string | undefined;
} | null {
  const width = asFiniteNumber(areaRaw['width']);
  const height = asFiniteNumber(areaRaw['height']);
  if (width === null || height === null || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height, name: asOptionalString(areaRaw['name']) };
}

function parseFloorPlanArea(areaRaw: Record<string, unknown>): UppFloorPlanParseDocumentResult {
  const room = parseAreaRoomFields(areaRaw);
  if (!room) {
    return {
      ok: false,
      errorCode: 'roomSize',
      message: 'area.width and area.height must be finite numbers > 0.',
    };
  }
  if (Object.prototype.hasOwnProperty.call(areaRaw, 'uppKonvaDraw')) {
    return {
      ok: false,
      errorCode: 'shapes',
      message: 'area.uppKonvaDraw is not supported; use area.shapes (array).',
    };
  }
  const orientationRaw = areaRaw['orientation'];
  let orientation: UppKonvaAreaOrientation | undefined;
  if (orientationRaw !== undefined && orientationRaw !== null) {
    if (typeof orientationRaw !== 'string') {
      return {
        ok: false,
        errorCode: 'orientation',
        message: 'Invalid area.orientation (expected string: PORTRAIT, LANDSCAPE, or AUTO).',
      };
    }
    const orientationOk = AREA_ORIENTATIONS.has(orientationRaw as UppKonvaAreaOrientation)
      ? (orientationRaw as UppKonvaAreaOrientation)
      : null;
    if (orientationOk === null) {
      return {
        ok: false,
        errorCode: 'orientation',
        message: 'Invalid area.orientation (use PORTRAIT, LANDSCAPE, or AUTO).',
      };
    }
    orientation = orientationOk;
  }
  const modeRaw = areaRaw['mode'];
  let mode: UppKonvaAreaSpatialMode | undefined;
  if (modeRaw !== undefined && modeRaw !== null) {
    if (typeof modeRaw !== 'string' || !AREA_SPATIAL_MODES.has(modeRaw as UppKonvaAreaSpatialMode)) {
      return {
        ok: false,
        errorCode: 'mode',
        message: 'Invalid area.mode (use SHAPES or VERTEX).',
      };
    }
    mode = modeRaw as UppKonvaAreaSpatialMode;
  }
  const u = areaRaw['shapes'];
  if (u === undefined || u === null) {
    return {
      ok: false,
      errorCode: 'shapes',
      message: 'area.shapes must be an array.',
    };
  }
  if (!Array.isArray(u)) {
    return {
      ok: false,
      errorCode: 'shapes',
      message: 'area.shapes must be an array.',
    };
  }
  const drawRaw: unknown[] = u;
  const shapes: UppKonvaShape[] = [];
  for (let i = 0; i < drawRaw.length; i += 1) {
    const row = parseDrawItem(drawRaw[i], i);
    if (!row) {
      return { ok: false, errorCode: 'drawItem', message: `Invalid shapes[${i}].` };
    }
    shapes.push(row);
  }
  const viewZoom = parseOptionalPositiveFinite(areaRaw, 'viewZoom');
  if (viewZoom === false) {
    return {
      ok: false,
      errorCode: 'viewZoom',
      message: 'area.viewZoom must be a finite number > 0 when present.',
    };
  }
  const marginPx = parseOptionalNonNegativeFinite(areaRaw, 'marginPx');
  if (marginPx === false) {
    return {
      ok: false,
      errorCode: 'marginPx',
      message: 'area.marginPx must be a finite number >= 0 when present.',
    };
  }
  const pixelRatioMax = parseOptionalPositiveFinite(areaRaw, 'pixelRatioMax');
  if (pixelRatioMax === false) {
    return {
      ok: false,
      errorCode: 'pixelRatioMax',
      message: 'area.pixelRatioMax must be a finite number > 0 when present.',
    };
  }
  const labelSizeRaw = areaRaw['labelSize'];
  let labelSize: string | undefined;
  if (labelSizeRaw !== undefined && labelSizeRaw !== null) {
    if (typeof labelSizeRaw !== 'string' || labelSizeRaw.trim() === '') {
      return {
        ok: false,
        errorCode: 'labelSize',
        message: 'area.labelSize must be a non-empty string when present.',
      };
    }
    labelSize = labelSizeRaw.trim();
  }
  const area: UppKonvaArea = {
    width: room.width,
    height: room.height,
    name: room.name,
    shapes,
    ...(orientation !== undefined ? { orientation } : {}),
    ...(mode !== undefined ? { mode } : {}),
    ...(viewZoom !== undefined ? { viewZoom } : {}),
    ...(marginPx !== undefined ? { marginPx } : {}),
    ...(pixelRatioMax !== undefined ? { pixelRatioMax } : {}),
    ...(labelSize !== undefined ? { labelSize } : {}),
  };
  return { ok: true, area };
}

/**
 * Before schema v11, `area.orientation` used the inverse mapping to `viewOrientationDeg`;
 * remap so imported documents keep the same on-screen rotation.
 */
function migrateAreaOrientationFromPreV11Schema(area: UppKonvaArea, schemaVersion: number): UppKonvaArea {
  if (schemaVersion >= UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION) {
    return area;
  }
  const o = area.orientation;
  if (o === 'LANDSCAPE') {
    return { ...area, orientation: 'PORTRAIT' };
  }
  if (o === 'PORTRAIT') {
    return { ...area, orientation: 'LANDSCAPE' };
  }
  return area;
}

export function parseFloorPlanDocument(json: string): UppFloorPlanParseDocumentResult {
  let root: unknown;
  try {
    root = JSON.parse(json) as unknown;
  } catch {
    return { ok: false, errorCode: 'json', message: 'Invalid JSON.' };
  }
  if (!isRecord(root)) {
    return { ok: false, errorCode: 'shape', message: 'Document must be a JSON object.' };
  }
  const kind = root['kind'];
  if (kind !== undefined && kind !== UPP_FLOOR_PLAN_DOCUMENT_KIND) {
    return { ok: false, errorCode: 'kind', message: 'Unrecognized document kind.' };
  }
  const schemaVersion = root['schemaVersion'];
  if (typeof schemaVersion !== 'number' || !Number.isInteger(schemaVersion)) {
    return {
      ok: false,
      errorCode: 'schemaVersion',
      message: `Missing or invalid schemaVersion (expected integer ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V9}, ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V10}, or ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION}).`,
    };
  }
  if (schemaVersion === UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V1) {
    return {
      ok: false,
      errorCode: 'schemaVersion',
      message: `schemaVersion 1 is not supported. Use area.shapes and schemaVersion ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION} (or ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V9} for legacy import).`,
    };
  }
  if (!SUPPORTED_SCHEMA_VERSIONS.has(schemaVersion)) {
    return {
      ok: false,
      errorCode: 'schemaVersion',
      message: `Unsupported schemaVersion ${schemaVersion} (supported: ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V9}, ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION_V10}, ${UPP_FLOOR_PLAN_DOCUMENT_SCHEMA_VERSION}).`,
    };
  }
  const areaRaw = root['area'];
  if (!isRecord(areaRaw)) {
    return { ok: false, errorCode: 'area', message: 'Missing or invalid "area" object.' };
  }
  const parsed = parseFloorPlanArea(areaRaw);
  if (!parsed.ok) {
    return parsed;
  }
  return { ok: true, area: migrateAreaOrientationFromPreV11Schema(parsed.area, schemaVersion) };
}
